// src/modules/chat/ChatDock.jsx
// Bottom-right floating dock — live/setup badges, dynamic entity reading

import { useState, useRef, useEffect } from 'react';
import { getEntity, getEntityGroups, ENTITY_TYPES, formatTime, getLastMessage } from './chatStore';
import { useChat } from './ChatContext';
import ChatWindow from './ChatWindow';

const TYPE_PILL = {
  [ENTITY_TYPES.MY_AI]:     { label: 'AGENT',    color: '#22d3ee' },
  [ENTITY_TYPES.PUBLIC_AI]: { label: 'PLATFORM', color: '#a78bfa' },
  [ENTITY_TYPES.HUMAN]:     { label: 'HUMAN',    color: '#4ade80' },
};

// ── New Chat Search Panel ─────────────────────────────────────────────────────
function NewChatPanel({ onClose }) {
  const { openChat, convos, ollamaOk } = useChat();
  const [q, setQ] = useState('');
  // Read fresh entity groups every render (picks up config changes)
  const { myAIs, publicAIs, humans } = getEntityGroups();
  const inputRef = useRef();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = (entities) =>
    q
      ? entities.filter(e =>
          e.name.toLowerCase().includes(q.toLowerCase()) ||
          e.role.toLowerCase().includes(q.toLowerCase()) ||
          (e.handle || '').toLowerCase().includes(q.toLowerCase())
        )
      : entities;

  const EntityRow = ({ entity }) => {
    const isAI  = entity.type !== ENTITY_TYPES.HUMAN;
    const last  = getLastMessage(convos[entity.id]);
    const tp    = TYPE_PILL[entity.type];
    const isLive = !entity.comingSoon && !!entity.brainId;

    const handleRowClick = () => {
      if (isLive) {
        openChat(entity.id);
        onClose();
      } else {
        window.dispatchEvent(new CustomEvent('dd:navigate', { detail: 'local_agents' }));
        onClose();
      }
    };

    return (
      <div
        onClick={handleRowClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 14px', cursor: 'pointer',
          transition: 'background .1s', borderRadius: 3,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#0a1120'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {/* Avatar */}
        <div style={{
          width: 36, height: 36,
          borderRadius: isAI ? 4 : '50%',
          background: `${entity.color}22`,
          border: `1px solid ${isLive ? entity.color + '66' : entity.color + '33'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, color: entity.color, fontWeight: 800,
          flexShrink: 0, position: 'relative',
          boxShadow: isLive ? `0 0 8px ${entity.color}18` : 'none',
        }}>
          {entity.avatar[0]}
          {/* Status dot */}
          <div style={{
            position: 'absolute', bottom: 0, right: 0,
            width: 8, height: 8, borderRadius: '50%',
            background: isLive ? entity.color : '#4ade8066',
            border: '2px solid #060b14',
            boxShadow: isLive ? `0 0 4px ${entity.color}` : 'none',
          }} />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>
              {entity.name}
            </span>
            {/* <span style={{
              fontSize: 7.5, padding: '1px 5px', borderRadius: 2,
              background: `${tp.color}18`, border: `1px solid ${tp.color}28`,
              color: tp.color, letterSpacing: '.07em', flexShrink: 0,
            }}>{tp.label}</span> */}
            {isLive ? (
              <span style={{
                fontSize: 7.5, padding: '1px 5px', borderRadius: 2,
                background: `${entity.color}18`, border: `1px solid ${entity.color}33`,
                color: entity.color, letterSpacing: '.07em', flexShrink: 0,
              }}>● LIVE</span>
            ) : (
              <span style={{
                fontSize: 7.5, padding: '1px 5px', borderRadius: 2,
                background: '#f59e0b12', border: '1px solid #f59e0b28',
                color: '#f59e0b', letterSpacing: '.07em', flexShrink: 0,
                cursor: 'pointer',
              }}>⚙ SETUP →</span>
            )}
          </div>
          <div style={{
            fontSize: 10, color: '#475569',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {last
              ? last.text.replace(/\*\*/g, '').slice(0, 52) + (last.text.length > 52 ? '…' : '')
              : isLive
                ? entity.role.split('·')[0].trim()
                : 'Needs configuration · Go to Local AI Agents'
            }
          </div>
        </div>

        {last && (
          <div style={{
            fontSize: 8, color: '#334155', flexShrink: 0,
            fontFamily: 'JetBrains Mono,monospace',
          }}>
            {formatTime(last.ts)}
          </div>
        )}
      </div>
    );
  };

  const Section = ({ label, entities, color }) => {
    const f = filtered(entities);
    if (f.length === 0) return null;
    return (
      <div style={{ marginBottom: 6 }}>
        <div style={{
          padding: '8px 14px 4px', fontSize: 8, color: '#334155',
          letterSpacing: '.16em', display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'JetBrains Mono,monospace',
        }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: color }} />
          {label} ({f.length})
          <span style={{ color, marginLeft: 4 }}>
            {f.filter(e => !e.comingSoon && e.brainId).length} live
          </span>
        </div>
        {f.map(e => <EntityRow key={e.id} entity={e} />)}
      </div>
    );
  };

  return (
    <div style={{
      position: 'absolute', bottom: 'calc(100% + 8px)', right: 0,
      width: 360, background: '#060b14',
      border: '1px solid #334155', borderRadius: 6,
      boxShadow: '0 -8px 40px rgba(0,0,0,.7)',
      animation: 'chatSlideUp .2s cubic-bezier(.16,1,.3,1)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 14px 10px',
        borderBottom: '1px solid #1e293b',
        background: '#020508',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: '#e2e8f0',
              letterSpacing: '.06em',
            }}>AGENTS</span>
            {/* Ollama health dot */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 8, color: ollamaOk === true ? '#4ade80' : ollamaOk === false ? '#f87171' : '#f59e0b',
              fontFamily: 'JetBrains Mono,monospace',
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%',
                background: ollamaOk === true ? '#4ade80' : ollamaOk === false ? '#f87171' : '#f59e0b',
                boxShadow: ollamaOk === true ? '0 0 6px #4ade80' : 'none',
              }} />
              {ollamaOk === true ? 'Ollama running' : ollamaOk === false ? 'Ollama offline' : 'checking…'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 13 }}
          >✕</button>
        </div>
        <input
          ref={inputRef}
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search agents…"
          style={{
            width: '100%', padding: '8px 10px', background: '#0a1120',
            border: '1px solid #1e293b', color: '#e2e8f0', fontSize: 11,
            borderRadius: 3, outline: 'none', fontFamily: 'monospace',
            boxSizing: 'border-box', transition: 'border-color .15s',
          }}
          onFocus={e => e.target.style.borderColor = '#22d3ee66'}
          onBlur={e => e.target.style.borderColor = '#1e293b'}
        />
        {ollamaOk === false && (
          <div style={{
            marginTop: 8, padding: '6px 8px',
            background: '#f59e0b08', border: '1px solid #f59e0b22', borderRadius: 3,
            fontSize: 8.5, color: '#f59e0b',
            fontFamily: 'JetBrains Mono,monospace', lineHeight: 1.6,
          }}>
            Start Ollama: <code style={{ color: '#22d3ee' }}>ollama serve</code>
            {' '}then configure agents in <strong>Local AI Agents</strong>
          </div>
        )}
      </div>

      {/* Agent list */}
      <div style={{ maxHeight: 400, overflowY: 'auto', padding: '6px 0' }}>
        <Section label="MY AGENTS"      entities={myAIs}     color="#22d3ee" />
        <Section label="PLATFORM AGENTS" entities={publicAIs} color="#a78bfa" />
        <Section label="RESEARCHERS"    entities={humans}    color="#4ade80" />
        {filtered([...myAIs, ...publicAIs, ...humans]).length === 0 && (
          <div style={{
            textAlign: 'center', padding: '24px',
            fontSize: 11, color: '#334155',
          }}>
            No results for "{q}"
          </div>
        )}
      </div>

      {/* Footer — link to Local AI Agents */}
      <div style={{
        padding: '10px 14px',
        borderTop: '1px solid #1e293b',
        background: '#020508',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 9, color: '#334155', fontFamily: 'JetBrains Mono,monospace' }}>
          Configure agents to bring them live
        </span>
        <button
          onClick={() => {
            window.dispatchEvent(new CustomEvent('dd:navigate', { detail: 'local_agents' }));
            onClose();
          }}
          style={{
            padding: '4px 10px', background: '#22d3ee18',
            border: '1px solid #22d3ee44', borderRadius: 2,
            color: '#22d3ee', fontSize: 8, cursor: 'pointer',
            fontFamily: 'JetBrains Mono,monospace', fontWeight: 700,
          }}
        >
          ⚙ LOCAL AI AGENTS →
        </button>
      </div>
    </div>
  );
}

// ── Collapsed chat tab in dock ────────────────────────────────────────────────
function CollapsedTab({ entityId, unreadCount }) {
  const { openChat, toggleMinimize, closeChat, windows } = useChat();
  // Always read fresh — so live status updates without re-opening
  const entity = getEntity(entityId);
  const win    = windows.find(w => w.entityId === entityId);
  const isAI   = entity?.type !== ENTITY_TYPES.HUMAN;
  const isLive = !entity?.comingSoon && !!entity?.brainId;

  if (!entity) return null;

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div
        title={`${entity.name} — ${isLive ? entity.brainId : 'needs configuration'}`}
        style={{
          width: 40, height: 40,
          borderRadius: isAI ? 4 : '50%',
          background: `${entity.color}22`,
          border: `2px solid ${isLive ? entity.color + '66' : entity.color + '44'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, color: entity.color, cursor: 'pointer',
          transition: 'all .15s', position: 'relative',
          boxShadow: isLive ? `0 0 10px ${entity.color}22` : 'none',
        }}
        onClick={() => win?.minimized ? toggleMinimize(entityId) : openChat(entityId)}
        onMouseEnter={e => {
          e.currentTarget.style.background = `${entity.color}33`;
          e.currentTarget.style.borderColor = entity.color;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = `${entity.color}22`;
          e.currentTarget.style.borderColor = isLive ? entity.color + '66' : entity.color + '44';
        }}
      >
        {entity.avatar[0]}
        {/* Status dot */}
        <div style={{
          position: 'absolute', bottom: 0, right: 0,
          width: 8, height: 8, borderRadius: '50%',
          background: isLive ? entity.color : '#4ade8066',
          border: '2px solid #020508',
          boxShadow: isLive ? `0 0 5px ${entity.color}` : 'none',
        }} />
        {/* Unread badge */}
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 16, height: 16,
            background: '#f87171', borderRadius: '50%',
            fontSize: 8, fontWeight: 800, color: '#020508',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #020508', padding: '0 3px',
          }}>{unreadCount}</div>
        )}
      </div>
      {/* Close button */}
      <button
        onClick={e => { e.stopPropagation(); closeChat(entityId); }}
        style={{
          position: 'absolute', top: -4, left: -4,
          width: 14, height: 14,
          background: '#1e293b', border: '1px solid #334155',
          borderRadius: '50%', color: '#f87171',
          fontSize: 7, cursor: 'pointer',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 1, display: 'none',
        }}
        className="dock-close-x"
      >✕</button>
      <style>{`
        .dock-tab-wrap:hover .dock-close-x { display: flex !important; }
      `}</style>
    </div>
  );
}

// ── MAIN DOCK ─────────────────────────────────────────────────────────────────
export default function ChatDock() {
  const {
    windows, convos, unread, totalUnread,
    searchOpen, setSearchOpen, ollamaOk,
  } = useChat();
  const [focusedId, setFocusedId] = useState(null);

  const openWins     = windows.filter(w => !w.minimized);
  const minimizedIds = windows.filter(w => w.minimized).map(w => w.entityId);
  const allIds       = windows.map(w => w.entityId);

  return (
    <>
      {/* ── Open chat windows ── */}
      <div style={{
        position: 'fixed', bottom: 64, right: 8,
        display: 'flex', flexDirection: 'row', alignItems: 'flex-end',
        gap: 8, zIndex: 300,
        maxWidth: 'calc(100vw - 20px)',
        overflowX: 'auto', overflowY: 'visible',
        paddingBottom: 4,
      }}>
        {openWins.map((win, i) => (
          <ChatWindow
            key={win.entityId}
            entityId={win.entityId}
            minimized={false}
            zIndex={focusedId === win.entityId ? 310 : 300 + i}
            onFocus={() => setFocusedId(win.entityId)}
          />
        ))}
      </div>

      {/* ── Dock bar ── */}
      <div style={{
        position: 'fixed', bottom: 0, right: 0,
        display: 'flex', alignItems: 'flex-end', gap: 6,
        padding: '0 12px 0 8px',
        background: 'linear-gradient(to right, transparent, #020508 30%)',
        zIndex: 400,
      }}>
        {/* Minimized windows as collapsed header bars */}
        {minimizedIds.map(id => (
          <div key={id} style={{ paddingBottom: 4 }}>
            <ChatWindow entityId={id} minimized={true} zIndex={350} />
          </div>
        ))}

        {minimizedIds.length > 0 && (
          <div style={{
            width: 1, height: 32, background: '#1e293b',
            margin: '0 4px', alignSelf: 'center',
          }} />
        )}

        {/* Collapsed tabs for open (non-minimized) windows */}
        <div style={{ display: 'flex', gap: 6, padding: '8px 0', alignItems: 'flex-end' }}>
          {allIds.filter(id => !minimizedIds.includes(id)).map(id => (
            <CollapsedTab key={id} entityId={id} unreadCount={unread[id] || 0} />
          ))}
        </div>

        {/* New chat / open agent list button */}
        <div style={{ position: 'relative', paddingBottom: 8 }}>
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            title="Open agents"
            style={{
              width: 40, height: 40, borderRadius: '50%',
              background: searchOpen ? '#22d3ee' : '#0a1120',
              border: `2px solid ${searchOpen ? '#22d3ee' : '#334155'}`,
              color: searchOpen ? '#020508' : '#22d3ee',
              fontSize: 18, cursor: 'pointer', transition: 'all .2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: searchOpen ? '0 0 20px #22d3ee44' : 'none',
              position: 'relative',
            }}
            onMouseEnter={e => {
              if (!searchOpen) {
                e.currentTarget.style.borderColor = '#22d3ee';
                e.currentTarget.style.background = '#22d3ee18';
              }
            }}
            onMouseLeave={e => {
              if (!searchOpen) {
                e.currentTarget.style.borderColor = '#334155';
                e.currentTarget.style.background = '#0a1120';
              }
            }}
          >
            {searchOpen ? '✕' : '✎'}
            {/* Total unread badge */}
            {totalUnread > 0 && !searchOpen && (
              <div style={{
                position: 'absolute', top: -2, right: -2,
                minWidth: 16, height: 16,
                background: '#f87171', borderRadius: '50%',
                fontSize: 8, fontWeight: 800, color: '#020508',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid #020508', padding: '0 3px',
              }}>{totalUnread}</div>
            )}
            {/* Ollama status micro-dot on button */}
            {ollamaOk !== null && (
              <div style={{
                position: 'absolute', bottom: 1, right: 1,
                width: 6, height: 6, borderRadius: '50%',
                background: ollamaOk ? '#4ade80' : '#f87171',
                border: '1.5px solid #020508',
              }} />
            )}
          </button>

          {searchOpen && <NewChatPanel onClose={() => setSearchOpen(false)} />}
        </div>
      </div>
    </>
  );
}
